import type { TeamWith } from "@redotech/account-common/base-team";
import { ManagedUserRoles } from "@redotech/account-common/roles";
import { GetUser } from "@redotech/account-common/user";
import type { BulkShipmentOneClickPrintDocumentSetting } from "@redotech/redo-fulfillment-common/bulk-shipment-print";
import { personalizedSlipsAccessible } from "@redotech/redo-fulfillment-common/personalized-slip/personalized-slip-access";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { PrintKind } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import {
  PrintableDocument,
  type BulkOneClickPrintPickListPosition,
} from "@redotech/redo-fulfillment-group-common/print-job";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { isWmsSotEnabledForLocation } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import { LocationId } from "@redotech/redo-inventory-id";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { BlockedPrintLabelGroup } from "@redotech/redo-merchant-fulfillment-rpc/schema/check-print-label-inventory";
import { Provider } from "@redotech/redo-order-common/provider";
import { groupHasClaimableShipment } from "@redotech/redo-package-protection-common/shipping-claims/shipping-claim";
import { showNotification, toast } from "@redotech/redo-web/alert";
import { assertNever } from "@redotech/util/type";
import { ProductBinInfo } from "./bin-selection-modal";
import {
  PinVerificationConfig,
  PinVerificationType,
} from "./pin-verification-context";
import {
  createDocumentPrintStatusMap,
  getDocumentPrintStatusMapForFulfillmentGroups,
  PrintBatchInput,
} from "./print-batch-modal";
import { getSkippedShipmentsNotificationInstance } from "./skipped-shipments-notification";
import { getWmsInventoryResolution } from "./wms-inventory-resolution";

// Helper function to get products without bins from fulfillment groups
const getProductsWithoutBins = async (
  client: RedoOutboundLabelsRpcClient,
  fulfillmentGroups: readonly FulfillmentGroupData[],
): Promise<FulfillmentOrderLineItem[]> => {
  const variantIdsToCheck = new Set<string>();
  const lineItemsByVariantId = new Map<string, FulfillmentOrderLineItem>();

  const shopifyFulfillmentGroups = fulfillmentGroups.filter((fg) =>
    fg.providers?.includes(Provider.SHOPIFY),
  );

  for (const group of shopifyFulfillmentGroups) {
    for (const lineItem of group.items) {
      const hasExistingBin = group.lineItemLocations?.some(
        (location) =>
          location.sku === lineItem.sku &&
          location.bins?.some((bin) => bin.bin),
      );

      if (!hasExistingBin && lineItem.variantId) {
        variantIdsToCheck.add(lineItem.variantId);
        lineItemsByVariantId.set(lineItem.variantId, lineItem);
      }
    }
  }

  if (variantIdsToCheck.size === 0) {
    return [];
  }

  try {
    const result = await client.getProductsWithoutBinMetafield({
      identifiers: Array.from(variantIdsToCheck),
    });

    const productsWithoutBins: FulfillmentOrderLineItem[] = [];
    for (const variantId of result.identifiers) {
      const lineItem = lineItemsByVariantId.get(variantId);
      if (lineItem) {
        productsWithoutBins.push(lineItem);
      }
    }

    return productsWithoutBins;
  } catch (error) {
    console.error("Error checking products without bins:", error);
    return [];
  }
};

function getBulkOneClickPrintableDocument(
  document: BulkShipmentOneClickPrintDocumentSetting["document"],
) {
  switch (document) {
    case "pickList":
      return PrintableDocument.PICK_LIST;
    case "label":
      return PrintableDocument.LABEL;
    default:
      assertNever(document);
  }
}

function getCreateAndPrintDocuments(
  bulkOneClickPrintDocuments: readonly BulkShipmentOneClickPrintDocumentSetting[],
) {
  const printDocuments: PrintableDocument[] = [];
  const seenDocuments = new Set<PrintableDocument>();
  for (const setting of bulkOneClickPrintDocuments) {
    if (!setting.enabled) {
      continue;
    }
    const document = getBulkOneClickPrintableDocument(setting.document);
    if (seenDocuments.has(document)) {
      continue;
    }
    seenDocuments.add(document);
    printDocuments.push(document);
  }

  return printDocuments;
}

// Determines whether the pick list should be stitched into the label PDF.
// This only applies when pick list and labels are both enabled; otherwise the
// pick list prints independently or not at all.
function getBulkOneClickPrintPickListPosition(
  printDocuments: readonly PrintableDocument[],
): BulkOneClickPrintPickListPosition | undefined {
  if (printDocuments.length < 2) {
    return undefined;
  }

  const labelIndex = printDocuments.indexOf(PrintableDocument.LABEL);
  const pickListIndex = printDocuments.indexOf(PrintableDocument.PICK_LIST);
  if (labelIndex < 0 || pickListIndex < 0) {
    return undefined;
  }

  return pickListIndex < labelIndex ? "first" : "last";
}

function getCreateAndPrintBatchInput({
  batch,
  document,
  documentPrintStatusMap,
  mixedPackingSlipSizes,
  packingSlipIds,
  personalizedSlipIds,
  bulkOneClickPrintPickListPosition,
}: {
  batch: PrintBatchInput["batch"];
  document: PrintableDocument;
  documentPrintStatusMap: PrintBatchInput["documentPrintStatusMap"];
  mixedPackingSlipSizes: boolean;
  packingSlipIds: string[];
  personalizedSlipIds: string[];
  bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition;
}): PrintBatchInput {
  const input: PrintBatchInput = { batch, document, documentPrintStatusMap };

  if (document === PrintableDocument.LABEL) {
    const labelPrintStatusMap: Partial<Record<PrintableDocument, boolean>> = {
      [PrintableDocument.LABEL]: true,
    };
    if (bulkOneClickPrintPickListPosition) {
      labelPrintStatusMap[PrintableDocument.PICK_LIST] = true;
      input.bulkOneClickPrintPickListPosition =
        bulkOneClickPrintPickListPosition;
    }
    input.documentPrintStatusMap =
      createDocumentPrintStatusMap(labelPrintStatusMap);
    input.mixedPackingSlipSizes = mixedPackingSlipSizes;
    input.packingSlipIds = packingSlipIds;
    input.personalizedSlipIds = personalizedSlipIds;
  }

  if (document === PrintableDocument.PACKING_SLIP) {
    input.packingSlipIds = packingSlipIds;
  }

  return input;
}

interface DoOMSActionParams {
  action: FulfillmentGroupActions | null;
  user: GetUser | undefined;
  client: RedoOutboundLabelsRpcClient;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  purchasableGroups: readonly FulfillmentGroupData[];
  team: TeamWith<{ outboundLabels: OutboundLabelSettings }> | undefined;
  resetSelections: () => void;
  reloadBalance: () => void;
  triggerRefresh: () => void;
  printBatch: (input: PrintBatchInput) => void;
  bulkOneClickPrintDocuments?: readonly BulkShipmentOneClickPrintDocumentSetting[];
  onGroupsFulfillment: (groupIds: string[]) => void;
  placeOnHold: () => void;
  addFunds: () => void;
  editManualOrder: () => void;
  openMarkAsFulfilledModal: (groups: readonly FulfillmentGroupData[]) => void;
  mixedPackingSlipSizes: boolean;
  mixedPersonalizedSlipSizes: boolean;
  submitCarrierClaim: () => void;
  startReturn: (orderId: string) => void;
  openCancelOrderModal: (
    groups: readonly FulfillmentGroupData[],
    onOrdersCancelled?: (groupIds: string[]) => void,
  ) => void;
  openPackingSlipBinModal?: (
    productsWithoutBins: FulfillmentOrderLineItem[],
    onProceedWithoutBin: () => void,
    onSaveAndContinue: () => void,
    onExit: () => void,
  ) => void;
  createReturnLabel: (fulfillmentGroup: FulfillmentGroupData) => void;
  openOverrideNotReceivedModal: () => void;
  openUnmergeModal?: () => void;
  openMissingItemsModal: () => void;
  openFreightBookingModal: () => void;
  createPurchaseOrder: (fulfillmentGroupId: string) => void;
  viewPurchaseOrder: (purchaseOrderId: string) => void;
  navigateToScanAndVerify: (fulfillmentGroupId: string) => void;
  deselectNonPurchasableGroups?: (groupIds: string[]) => void;
  openBinSelectionModal?: (params: {
    lineItems: {
      productId: string;
      productName: string;
      requiredQuantity: number;
      bins: {
        storageLocationId: string;
        binName: string;
        availableQuantity: number;
      }[];
    }[];
    fulfillmentGroupIds: string[];
    onConfirm: () => void;
    onBypass?: () => void;
    bypassLabel?: string;
  }) => void;
  openInventoryAdjustmentWarningModal?: (params: {
    lineItems: ProductBinInfo[];
    onConfirm: () => void;
    onBypass?: () => void;
    bypassLabel?: string;
  }) => void;
  openInsufficientInventoryModal: (params: {
    printableCount: number;
    blockedGroups: BlockedPrintLabelGroup[];
    onConfirm: () => void;
    onPrintAll: () => void;
  }) => void;
  executeWithPinCheck?: (
    action: () => void,
    config: PinVerificationConfig,
    modalOptions?: { title?: string; description?: string },
  ) => void;
  deviceLocationId: LocationId | undefined;
}

export const doOMSAction = async (
  {
    action,
    user,
    client,
    fulfillmentGroups,
    purchasableGroups,
    team,
    resetSelections,
    reloadBalance,
    placeOnHold,
    addFunds,
    triggerRefresh,
    printBatch,
    bulkOneClickPrintDocuments,
    onGroupsFulfillment,
    openMarkAsFulfilledModal,
    editManualOrder,
    mixedPackingSlipSizes,
    mixedPersonalizedSlipSizes,
    submitCarrierClaim,
    startReturn,
    openPackingSlipBinModal,
    openCancelOrderModal,
    createReturnLabel,
    openOverrideNotReceivedModal,
    openUnmergeModal,
    openMissingItemsModal,
    openFreightBookingModal,
    createPurchaseOrder,
    viewPurchaseOrder,
    navigateToScanAndVerify,
    deselectNonPurchasableGroups,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
    openInsufficientInventoryModal,
    executeWithPinCheck,
    deviceLocationId,
  }: DoOMSActionParams,
  signal?: AbortSignal,
): Promise<void> => {
  const packingSlipIds = fulfillmentGroups
    .map((g) => {
      if (g.packingSlipTemplateId) {
        return g.packingSlipTemplateId;
      }
      return team?.settings?.outboundLabels?.packingSlipId;
    })
    .filter((id): id is string => id !== undefined);

  const personalizedSlipsEnabled = personalizedSlipsAccessible(
    team?.settings?.outboundLabels,
  );

  const personalizedSlipIds = personalizedSlipsEnabled
    ? fulfillmentGroups
        .map((g) => g.personalizedSlipTemplateId)
        .filter((id): id is string => id !== undefined)
    : [];
  const documentPrintStatusMap =
    getDocumentPrintStatusMapForFulfillmentGroups(fulfillmentGroups);

  switch (action) {
    case FulfillmentGroupActions.CREATE_LABEL:
    case FulfillmentGroupActions.CREATE_AND_PRINT_LABEL: {
      // TODO: make sure we're ready to buy
      if (!user) {
        throw new Error("User not found - cannot buy labels");
      }

      if (purchasableGroups.length === 0) {
        throw new Error("No fulfillment groups are ready for label purchase");
      }

      const purchasableGroupIds = new Set(purchasableGroups.map((g) => g._id));
      const nonPurchasableGroups = fulfillmentGroups.filter(
        (g) => !purchasableGroupIds.has(g._id),
      );

      if (nonPurchasableGroups.length > 0) {
        deselectNonPurchasableGroups?.(nonPurchasableGroups.map((g) => g._id));

        showNotification(
          getSkippedShipmentsNotificationInstance(
            purchasableGroups,
            nonPurchasableGroups,
          ),
        );
      }

      const purchaseLabels = async (
        groupsToPurchase: readonly FulfillmentGroupData[] = purchasableGroups,
      ) => {
        try {
          const createAndPrintDocuments =
            action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL &&
            bulkOneClickPrintDocuments
              ? getCreateAndPrintDocuments(bulkOneClickPrintDocuments)
              : [PrintableDocument.LABEL];
          const bulkOneClickPrintPickListPosition =
            getBulkOneClickPrintPickListPosition(createAndPrintDocuments);
          const shouldPrintLabels =
            action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL &&
            createAndPrintDocuments.includes(PrintableDocument.LABEL);
          const buyRequest = {
            buyRequests: groupsToPurchase.map((fulfillmentGroup) => ({
              fulfillmentGroupId: fulfillmentGroup._id,
              shipmentRate: fulfillmentGroup.selectedShippingRate!,
              lineItems: fulfillmentGroup.items.map((item) => ({
                id: item.externalId,
                quantity: item.unfulfilledQuantity,
              })),
            })),
            userId: user._id,
            print: shouldPrintLabels,
            initiationTime: Date.now(),
          };

          const response = await client.buyLabels(buyRequest, { signal });
          if (action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL) {
            const batch = {
              batchId: response.batchId,
              batchNumber: response.batchNumber,
            };
            if (!bulkOneClickPrintDocuments) {
              printBatch({
                batch,
                document: PrintableDocument.LABEL,
                documentPrintStatusMap: createDocumentPrintStatusMap({
                  [PrintableDocument.LABEL]: true,
                }),
                mixedPackingSlipSizes,
                packingSlipIds,
                personalizedSlipIds,
              });
              return;
            }
            if (bulkOneClickPrintPickListPosition) {
              printBatch(
                getCreateAndPrintBatchInput({
                  batch,
                  document: PrintableDocument.LABEL,
                  documentPrintStatusMap,
                  mixedPackingSlipSizes,
                  packingSlipIds,
                  personalizedSlipIds,
                  bulkOneClickPrintPickListPosition,
                }),
              );
              return;
            }
            for (const document of createAndPrintDocuments) {
              printBatch(
                getCreateAndPrintBatchInput({
                  batch,
                  document,
                  documentPrintStatusMap,
                  mixedPackingSlipSizes,
                  packingSlipIds,
                  personalizedSlipIds,
                }),
              );
            }
          }
        } catch (e) {
          toast("Error purchasing labels", { variant: "error" });
          console.error("Error purchasing labels", (e as Error).message);
        } finally {
          onGroupsFulfillment(groupsToPurchase.map((g) => g._id));
          reloadBalance();
        }
      };

      // Run the WMS bin check (if enabled) then buy labels for the given groups.
      const finalizePurchase = async (
        groupsToPurchase: readonly FulfillmentGroupData[],
      ) => {
        const wmsSourceOfTruthEnabled = isWmsSotEnabledForLocation(
          team?.settings?.outboundLabels?.wms,
          deviceLocationId,
        );

        if (
          wmsSourceOfTruthEnabled &&
          (openBinSelectionModal || openInventoryAdjustmentWarningModal)
        ) {
          try {
            const fgIds = groupsToPurchase.map((fg) => fg._id);
            const { lineItems: availableBins, manualPickFulfillmentGroupIds } =
              await client.getAvailableBinsForFulfillmentGroups(
                { fulfillmentGroupIds: fgIds },
                { signal },
              );

            const wmsInventoryResolution =
              getWmsInventoryResolution(availableBins);

            const isAdmin = user?.managedUserRoles?.includes(
              ManagedUserRoles.ADMIN,
            );
            const pinRequired =
              !isAdmin &&
              !!team?.settings?.outboundLabels?.pinChecks?.bypassWmsSotCheck
                ?.enabled &&
              !!team?.settings?.outboundLabels?.pinChecks?.pin;
            const bypassLabel = pinRequired ? "Bypass with PIN" : "Bypass";

            const bypassAction = () => {
              if (executeWithPinCheck) {
                executeWithPinCheck(
                  () => {
                    void purchaseLabels(groupsToPurchase);
                  },
                  { type: PinVerificationType.BypassWmsSotCheck },
                  {
                    title: "Bypass Bin Selection",
                    description:
                      "Enter your PIN to bypass the bin selection check and proceed with label purchase.",
                  },
                );
              } else {
                void purchaseLabels(groupsToPurchase);
              }
            };

            if (
              wmsInventoryResolution === "bin-selection" &&
              openBinSelectionModal
            ) {
              openBinSelectionModal({
                lineItems: availableBins,
                fulfillmentGroupIds: manualPickFulfillmentGroupIds,
                onConfirm: () => {
                  void purchaseLabels(groupsToPurchase);
                },
                onBypass: bypassAction,
                bypassLabel,
              });
              return;
            }

            if (
              wmsInventoryResolution === "inventory-adjustment-warning" &&
              openInventoryAdjustmentWarningModal
            ) {
              openInventoryAdjustmentWarningModal({
                lineItems: availableBins,
                onConfirm: () => {
                  void purchaseLabels(groupsToPurchase);
                },
                onBypass: bypassAction,
                bypassLabel,
              });
              return;
            }
          } catch (e) {
            toast("Error checking bin availability", { variant: "error" });
            console.error(
              "Error checking bin availability",
              (e as Error).message,
            );
            return;
          }
        }

        await purchaseLabels(groupsToPurchase);
      };

      // Opt-in inventory gate (OMS-3373): only runs when the merchant enables it
      // in Order Management > General. Off by default; print flow is unchanged.
      if (team?.settings?.outboundLabels?.blockPrintingBeyondInventory) {
        try {
          const inventoryCheck = await client.checkPrintLabelInventory(
            { fulfillmentGroupIds: purchasableGroups.map((g) => g._id) },
            { signal },
          );
          if (inventoryCheck.blockedGroups.length > 0) {
            const printableIds = new Set(inventoryCheck.printableGroupIds);
            const printableGroups = purchasableGroups.filter((g) =>
              printableIds.has(g._id),
            );
            openInsufficientInventoryModal({
              printableCount: printableGroups.length,
              blockedGroups: inventoryCheck.blockedGroups,
              onConfirm: () => {
                void finalizePurchase(printableGroups);
              },
              onPrintAll: () => {
                void finalizePurchase(purchasableGroups);
              },
            });
            break;
          }
        } catch (e) {
          // Fail open: if the inventory check is unavailable, print as before
          // rather than blocking a core workflow.
          console.error(
            "Error checking inventory availability",
            (e as Error).message,
          );
        }
      }

      await finalizePurchase(purchasableGroups);
      break;
    }
    case FulfillmentGroupActions.READY_FOR_PICKUP: {
      if (!user) {
        throw new Error("User not found - cannot ready for pickup");
      }

      let toastMsg = "";
      let toastVariant: "success" | "error" | "warning" = "success";
      try {
        const response = await client.markAsReadyForPickup({
          fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
          userId: user._id,
        });
        toastMsg = `Batch #${response.batchNumber} marked as ready for pickup`;

        // If local pickup label printing is enabled, print the label
        if (
          team?.settings?.outboundLabels?.documents?.localPickupLabel?.enabled
        ) {
          printBatch({
            batch: {
              batchId: response.batchId,
              batchNumber: response.batchNumber,
            },
            document: PrintableDocument.LOCAL_PICK_UP_LABEL,
            documentPrintStatusMap: createDocumentPrintStatusMap({
              [PrintableDocument.LOCAL_PICK_UP_LABEL]: true,
            }),
          });
        }
      } catch (e) {
        toastMsg = "Error marking as ready for pickup";
        toastVariant = "error";
      } finally {
        onGroupsFulfillment(fulfillmentGroups.map((g) => g._id));
        toast(toastMsg, { variant: toastVariant });
      }
      break;
    }
    case FulfillmentGroupActions.MARK_AS_PICKED_UP: {
      let toastMsg = "";
      let toastVariant: "success" | "error" | "warning" = "success";
      try {
        await client.markAsPickedUp({
          fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
        });
        toastMsg = "Orders marked as picked up";
      } catch (e) {
        toastMsg = "Error marking as picked up";
        toastVariant = "error";
      } finally {
        onGroupsFulfillment(fulfillmentGroups.map((g) => g._id));
        toast(toastMsg, { variant: toastVariant });
      }
      break;
    }
    case FulfillmentGroupActions.MARK_AS_FULFILLED: {
      const doMarkAsFulfilled = () =>
        openMarkAsFulfilledModal(fulfillmentGroups);
      if (executeWithPinCheck) {
        executeWithPinCheck(doMarkAsFulfilled, {
          type: PinVerificationType.MarkFulfilled,
        });
      } else {
        doMarkAsFulfilled();
      }
      break;
    }
    case FulfillmentGroupActions.PRINT_LABEL: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.LABEL,
        documentPrintStatusMap,
        somePrinted: fulfillmentGroups.some(
          (g) => g.printStatus?.[PrintKind.LABEL],
        ),
        mixedPackingSlipSizes,
        packingSlipIds,
        personalizedSlipIds,
      });
      break;
    }
    case FulfillmentGroupActions.PRINT_PACKING_SLIP: {
      console.error("Clicked print packing slip for groups", {
        fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
        team: team?._id,
      });

      const doPrintPackingSlip = () => {
        printBatch({
          batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
          document: PrintableDocument.PACKING_SLIP,
          documentPrintStatusMap,
          somePrinted: fulfillmentGroups.some(
            (g) => g.printStatus?.[PrintKind.PACKING_SLIP],
          ),
          packingSlipIds,
        });
      };

      // Check if we should show the bin assignment modal
      if (
        team?.settings?.outboundLabels?.canUpdateBinsFromTable &&
        openPackingSlipBinModal
      ) {
        try {
          const productsWithoutBins = await getProductsWithoutBins(
            client,
            fulfillmentGroups,
          );

          if (productsWithoutBins.length > 0) {
            // Show the packing slip bin modal
            openPackingSlipBinModal(
              productsWithoutBins,
              doPrintPackingSlip, // onProceedWithoutBin
              doPrintPackingSlip, // onSaveAndContinue
              () => {}, // onExit - do nothing
            );
            break;
          }
        } catch (error) {
          console.error("Error checking for products without bins:", error);
          // If there's an error checking bins, just proceed with printing
        }
      }

      // If no bin check needed or no products without bins, proceed with printing
      doPrintPackingSlip();
      break;
    }
    case FulfillmentGroupActions.PRINT_PERSONALIZED_SLIP: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.PERSONALIZED_SLIP,
        documentPrintStatusMap,
        skipPrinter: mixedPersonalizedSlipSizes,
        personalizedSlipIds,
      });

      break;
    }
    case FulfillmentGroupActions.PRINT_PICK_LIST: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.PICK_LIST,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.PRINT_ORDER_SUMMARY: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.ORDER_SUMMARY,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.COMMERCIAL_INVOICE: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.COMMERCIAL_INVOICE,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.PLACE_HOLD: {
      placeOnHold();
      break;
    }
    case FulfillmentGroupActions.RELEASE_HOLD: {
      let toastMsg = "";
      let toastVariant: "success" | "error" | "warning" = "success";
      const { failedGroupIds } = await client.releaseAllHolds({
        fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
      });
      if (failedGroupIds.length > 0) {
        toastMsg = `Failed to release holds from ${failedGroupIds.length} shipments`;
        toastVariant = "error";
      } else {
        toastVariant = "success";
      }
      toast(toastMsg, { variant: toastVariant });
      break;
    }
    case FulfillmentGroupActions.UNMERGE: {
      if (fulfillmentGroups.length === 1 && openUnmergeModal) {
        openUnmergeModal();
      } else {
        let toastMsg = "Successfully unmerged shipments";
        let toastVariant: "success" | "error" | "warning" = "success";
        try {
          await client.unmergeFulfillmentGroups({
            fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
          });
          toast(toastMsg, { variant: toastVariant });
        } catch (error) {
          toastMsg = "Error unmerging shipments";
          toastVariant = "error";
          toast(toastMsg, { variant: toastVariant });
        }
        triggerRefresh();
        resetSelections();
      }
      break;
    }
    case FulfillmentGroupActions.ADD_FUNDS_AND_CREATE: {
      addFunds();
      break;
    }
    case FulfillmentGroupActions.EDIT_ORDER: {
      editManualOrder();
      break;
    }
    case FulfillmentGroupActions.NOTIFY_MARKETPLACE: {
      if (fulfillmentGroups.length !== 1) {
        toast("Can only notify marketplace for one shipment at a time", {
          variant: "error",
        });
        break;
      }

      let toastMsg = "";
      let toastVariant: "success" | "error" | "warning" = "success";
      try {
        await client.notifyMarketplace({
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          fulfillmentGroupId: fulfillmentGroups[0]._id,
        });
        toastMsg = "Marketplace notification sent";
      } catch (e) {
        toastMsg = "Error sending marketplace notification";
        toastVariant = "error";
        console.error(
          "Error sending marketplace notification",
          (e as Error).message,
        );
      } finally {
        toast(toastMsg, { variant: toastVariant });
      }
      break;
    }
    case FulfillmentGroupActions.SUBMIT_CARRIER_CLAIM: {
      if (
        fulfillmentGroups.length !== 1 ||
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        !groupHasClaimableShipment(fulfillmentGroups[0])
      ) {
        toast("Can only submit carrier claim for one shipment at a time", {
          variant: "error",
        });
        break;
      }

      submitCarrierClaim();
      break;
    }
    case FulfillmentGroupActions.EXPORT_PICK_LIST_CSV: {
      const response = await client.getPickListCsv({
        fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
      });
      const url = response.url;
      const link = document.createElement("a");
      link.href = url;
      link.download = "pick_list.csv";
      document.body.append(link);
      link.click();
      link.remove();
      break;
    }
    case FulfillmentGroupActions.CREATE_RETURN_LABEL: {
      if (fulfillmentGroups.length !== 1) {
        toast("Can only create return label for one shipment at a time", {
          variant: "error",
        });
        break;
      }

      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      createReturnLabel(fulfillmentGroups[0]);
      break;
    }
    case FulfillmentGroupActions.PRINT_RETURN_LABEL: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.RETURN_LABEL,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.PRINT_LOCAL_PICKUP_LABEL: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.LOCAL_PICK_UP_LABEL,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.OVERRIDE_NOT_RECEIVED: {
      openOverrideNotReceivedModal();
      break;
    }
    case FulfillmentGroupActions.RESOLVE_PROBLEM_ORDER: {
      if (fulfillmentGroups.length !== 1) {
        toast("Can only resolve problem order at a time", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 3 }),
        });
      } else {
        openMissingItemsModal();
      }
      break;
    }
    case FulfillmentGroupActions.BOOK_FREIGHT: {
      openFreightBookingModal();
      break;
    }
    case FulfillmentGroupActions.PRINT_BOL: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.BILL_OF_LADING,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.CREATE_PURCHASE_ORDER: {
      if (fulfillmentGroups.length === 1 && fulfillmentGroups[0]) {
        const groupId = fulfillmentGroups[0]._id;
        const doCreatePurchaseOrder = () => createPurchaseOrder(groupId);
        if (executeWithPinCheck) {
          executeWithPinCheck(doCreatePurchaseOrder, {
            type: PinVerificationType.CreatePurchaseOrder,
          });
        } else {
          doCreatePurchaseOrder();
        }
      }
      break;
    }
    case FulfillmentGroupActions.VIEW_PURCHASE_ORDER: {
      if (fulfillmentGroups.length === 1 && fulfillmentGroups[0]) {
        const purchaseOrderId = fulfillmentGroups[0].linkedPurchaseOrderId;
        if (purchaseOrderId) {
          viewPurchaseOrder(purchaseOrderId);
        }
      }
      break;
    }
    case FulfillmentGroupActions.SCAN_AND_VERIFY: {
      if (fulfillmentGroups.length === 1 && fulfillmentGroups[0]) {
        navigateToScanAndVerify(fulfillmentGroups[0]._id);
      }
      break;
    }
    case FulfillmentGroupActions.PRINT_PARCEL_PACKING_SLIPS: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.PARCEL_PACKING_SLIP,
        documentPrintStatusMap,
      });
      break;
    }
    default:
      if (action) {
        assertNever(action);
      }
  }
};
